import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Container, Header, List, Icon, Button, Segment, Pagination, Dropdown, Divider } from "semantic-ui-react";
import moment from "moment";
import * as notificationActions from "./notificationActions";
import { getUserInfo } from "../../../api/axios";
import { isSubscriptionSuspended, fetchSubscriptionData } from "../../../utils/subscriptionUtils";
import SubscriptionRestriction from "../../common/subscriptionRestriction/SubscriptionRestriction";
import "../../domain/company/companyOffices/officeDashboard/officeDashboard.scss";
import "./NotificationsScreen.css";

class NotificationsScreen extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentPage: 1,
            itemsPerPage: 100,
            filterType: 'all',
            sortBy: 'newest',
            hasMoreNotifications: true,
            loadedCount: 0,
            isLoadingMore: false,
            isRefreshing: false,
            isSubscriptionSuspended: false
        };
        this.refreshTimeout = null;
    }

    componentDidMount() {
        this.setState({ loadedCount: 0, hasMoreNotifications: true, isLoadingMore: false });
        (async () => {
            try {
                const subscriptionData = await fetchSubscriptionData();
                if (subscriptionData && isSubscriptionSuspended(subscriptionData)) {
                    this.setState({ isSubscriptionSuspended: true });
                    return;
                }
            } catch (e) {
                // proceed to load notifications if check fails
            }
            this.loadNotifications();
        })();
    }

    componentWillUnmount() {
        // Mark all notifications as seen when leaving the notifications screen
        const { notificationReducer } = this.props;
        const { unreadCount } = notificationReducer;

        if (unreadCount > 0) {
            this.props.notificationActions.markAllNotificationsAsSeen();
        }
    }

    componentDidUpdate(prevProps) {
        const currentUser = getUserInfo();
        const prevUser = prevProps.currentUser;

        if (currentUser?.id !== prevUser?.id) {
            this.setState({ loadedCount: 0, hasMoreNotifications: true, isRefreshing: false });
            this.loadNotifications();
        }

        // Update loaded count and hasMore flag when notifications change
        const { notifications, loading } = this.props.notificationReducer;
        const { notifications: prevNotifications, loading: prevLoading } = prevProps.notificationReducer;

        // Only update state when notifications actually change and loading is complete
        if (notifications !== prevNotifications && !loading && prevLoading) {
            const newCount = notifications.length;
            const { isLoadingMore, isRefreshing } = this.state;
            const previousCount = prevNotifications ? prevNotifications.length : 0;

            console.log('Notifications updated:', {
                newCount,
                isLoadingMore,
                isRefreshing,
                previousCount,
                stateLoadedCount: this.state.loadedCount
            });

            // Determine if there are more notifications to load
            let hasMore = false;
            if (isLoadingMore) {
                // If we were loading more and got exactly 50 new notifications, there might be more
                const newNotificationsCount = newCount - previousCount;
                hasMore = newNotificationsCount >= 50;
            } else {
                // Initial load or refresh - if we got exactly 50, there might be more
                hasMore = newCount >= 50;
            }

            this.setState({
                loadedCount: newCount,
                hasMoreNotifications: hasMore,
                isLoadingMore: false,
                isRefreshing: false
            });
        } else if (loading && !prevLoading) {
            // Loading just started - don't update state yet
            console.log('Loading started...');
        }
    }

    loadNotifications = (loadMore = false) => {
        const { loadedCount, isRefreshing, isLoadingMore } = this.state;
        const { loading } = this.props.notificationReducer;

        // Clear any pending refresh timeout
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
            this.refreshTimeout = null;
        }

        // Prevent multiple simultaneous refresh calls
        if (!loadMore && (isRefreshing || loading)) {
            console.log('Refresh already in progress, skipping...');
            return;
        }

        // Prevent multiple simultaneous load more calls
        if (loadMore && isLoadingMore) {
            console.log('Load more already in progress, skipping...');
            return;
        }

        const start = loadMore ? loadedCount : 0;
        const page = loadMore ? Math.floor(loadedCount / 50) + 1 : 1;

        if (loadMore) {
            this.setState({ isLoadingMore: true });
        } else {
            // Reset state for refresh - clear notifications first to show loading state
            this.setState({
                isRefreshing: true,
                loadedCount: 0,
                hasMoreNotifications: true,
                currentPage: 1
            });
            // Clear notifications in reducer to show loading state during refresh
            this.props.notificationActions.clearNotifications();
        }

        // Use 50 as take parameter for NotificationsScreen
        this.props.notificationActions.getUserNotifications(start, page, 50);
    };

    handleMarkAllAsSeen = () => {
        this.props.notificationActions.markAllNotificationsAsSeen();
    };

    handleNotificationClick = (notification) => {
        if (notification.actionUrl) {
            this.props.history.push(notification.actionUrl);
        }
    };

    handlePageChange = (event, { activePage }) => {
        this.setState({ currentPage: activePage });
    };

    handleLoadMore = () => {
        this.loadNotifications(true);
    };

    handleFilterChange = (event, { value }) => {
        this.setState({ filterType: value, currentPage: 1 });
    };

    handleSortChange = (event, { value }) => {
        this.setState({ sortBy: value, currentPage: 1 });
    };

    getFilteredAndSortedNotifications = () => {
        const { notifications } = this.props.notificationReducer;
        const { filterType, sortBy } = this.state;

        let filtered = notifications;

        // Filter by type
        if (filterType !== 'all') {
            filtered = notifications.filter(notification => notification.type === filterType);
        }

        // Sort notifications
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                case 'oldest':
                    return new Date(a.createdAt) - new Date(b.createdAt);
                case 'unread':
                    if (a.isSeen === b.isSeen) {
                        return new Date(b.createdAt) - new Date(a.createdAt);
                    }
                    return a.isSeen ? 1 : -1;
                default:
                    return new Date(b.createdAt) - new Date(a.createdAt);
            }
        });

        return filtered;
    };

    render() {
        const { notificationReducer } = this.props;
        const { notifications, unreadCount, loading } = notificationReducer;
        const { currentPage, itemsPerPage, filterType, sortBy, hasMoreNotifications, loadedCount, isLoadingMore, isSubscriptionSuspended } = this.state;

        // If subscription is suspended, show restriction message
        if (isSubscriptionSuspended) {
            return (
                <div style={{ padding: "20px" }}>
                    <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessNotifications" />
                </div>
            );
        }

        const filteredNotifications = this.getFilteredAndSortedNotifications();
        const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentNotifications = filteredNotifications.slice(startIndex, endIndex);

        console.log('Render state:', {
            loadedCount,
            hasMoreNotifications,
            isLoadingMore,
            filteredNotificationsLength: filteredNotifications.length,
            notificationsLength: notifications.length,
            shouldShowLoadMore: hasMoreNotifications && filteredNotifications.length > 0
        });

        const filterOptions = [
            { key: 'all', text: this.props.t("app.notifications.allNotifications"), value: 'all' },
            { key: 'leave_request', text: this.props.t("app.notifications.leaveRequests"), value: 'leave_request' },
            { key: 'approval', text: this.props.t("app.notifications.approvals"), value: 'approval' },
            { key: 'system', text: this.props.t("app.notifications.system"), value: 'system' },
            { key: 'reminder', text: this.props.t("app.notifications.reminders"), value: 'reminder' },
            { key: 'update', text: this.props.t("app.notifications.updates"), value: 'update' },
            { key: 'alert', text: this.props.t("app.notifications.alerts"), value: 'alert' }
        ];

        const sortOptions = [
            { key: 'newest', text: this.props.t("app.notifications.newestFirst"), value: 'newest' },
            { key: 'oldest', text: this.props.t("app.notifications.oldestFirst"), value: 'oldest' },
            { key: 'unread', text: this.props.t("app.notifications.unreadFirst"), value: 'unread' }
        ];

        return (
            <Container className="py-4 px-3 px-md-4 notifications-screen" fluid>
                <div className="office-header-bar">
                    <div className="office-header-content">
                        <div className="office-header-left">
                            <div className="office-header-icon-wrapper">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="white"
                                    viewBox="0 0 16 16"
                                    style={{ margin: 0 }}
                                >
                                    <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zM8 1.918l-.797.161A4.002 4.002 0 0 0 4 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.463 2.285h10.24c-.087-.72-.303-1.518-.463-2.285C12.134 8.197 12 6.628 12 6a4.002 4.002 0 0 0-3.203-3.92L8 1.917zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6c0-2.42 1.72-4.44 4.005-4.901a1 1 0 1 1 1.99 0A5.002 5.002 0 0 1 13 6c0 .88.32 4.2 1.22 6z" />
                                </svg>
                            </div>
                            <div className="office-header-text">
                                <h1 className="office-page-title">
                                    {this.props.t("app.notifications.title")}
                                    {unreadCount > 0 && (
                                        <span className="notifications-unread-count">{unreadCount}</span>
                                    )}
                                </h1>
                                <p className="office-page-description">
                                    {this.props.t("app.notifications.description")}
                                </p>
                            </div>
                        </div>
                        <div className="notifications-header-right">
                            <button
                                className="btn btn-outline-secondary notifications-refresh-btn"
                                onClick={() => this.loadNotifications(false)}
                                disabled={loading || this.state.isRefreshing}
                                style={{
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    fontWeight: '500',
                                    borderWidth: '2px',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                {loading ? (
                                    <Icon name="spinner" loading style={{ margin: 0, fontSize: '16px' }} />
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        viewBox="0 0 16 16"
                                        style={{ margin: 0, display: 'inline-block' }}
                                    >
                                        <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
                                        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                                    </svg>
                                )}
                                {this.props.t("app.notifications.refresh")}
                            </button>
                            {unreadCount > 0 && (
                                <button
                                    className="btn btn-outline-secondary notifications-mark-all-btn"
                                    onClick={this.handleMarkAllAsSeen}
                                    style={{
                                        borderRadius: '10px',
                                        padding: '10px 20px',
                                        fontWeight: '500',
                                        borderWidth: '2px',
                                        whiteSpace: 'nowrap',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        viewBox="0 0 16 16"
                                        style={{ margin: 0, display: 'inline-block' }}
                                    >
                                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                        <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z" />
                                    </svg>
                                    {this.props.t("app.notifications.markAllRead")}
                                </button>
                            )}
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => this.props.history.goBack()}
                                style={{
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    fontWeight: '500',
                                    borderWidth: '2px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <i className="bi bi-arrow-left me-2"></i>{this.props.t("app.notifications.back")}
                            </button>
                        </div>
                    </div>
                </div>

                <Segment className="notifications-controls" style={{ display: 'none' }}>
                    <div className="controls-row">
                        <div className="filter-controls" style={{ display: 'none' }}>
                            <Dropdown
                                selection
                                options={filterOptions}
                                value={filterType}
                                onChange={this.handleFilterChange}
                                placeholder={this.props.t("app.notifications.filterByType")}
                                className="filter-dropdown"
                            />
                            <Dropdown
                                selection
                                options={sortOptions}
                                value={sortBy}
                                onChange={this.handleSortChange}
                                placeholder={this.props.t("app.notifications.sortBy")}
                                className="sort-dropdown"
                            />
                        </div>
                    </div>
                </Segment>

                <Segment className="notifications-content">
                    {loading ? (
                        <div className="loading-state">
                            <Icon name="spinner" loading size="large" />
                            <p>{this.props.t("app.notifications.loadingNotifications")}</p>
                        </div>
                    ) : filteredNotifications.length === 0 ? (
                        <div className="empty-state">
                            <Icon name="bell outline" size="huge" color="grey" />
                            <h3>{this.props.t("app.notifications.noNotificationsFound")}</h3>
                            <p>
                                {filterType === 'all'
                                    ? this.props.t("app.notifications.noNotificationsYet")
                                    : this.props.t("app.notifications.noFilteredNotifications", { filter: filterType.replace('_', ' ') })
                                }
                            </p>
                        </div>
                    ) : (
                        <>
                            <List divided relaxed className="notifications-list">
                                {currentNotifications.map((notification) => (
                                    <List.Item
                                        key={notification.id}
                                        className={`notification-item ${!notification.isSeen ? 'unread' : ''}`}
                                        onClick={() => this.handleNotificationClick(notification)}
                                    >
                                        <List.Content>
                                            <div className="notification-content">
                                                {!notification.isSeen && (
                                                    <div className="unread-indicator"></div>
                                                )}
                                                <div className="notification-body">
                                                    <div className="notification-message">
                                                        {notification.message}
                                                    </div>
                                                    {notification.type && (
                                                        <div className="notification-type">
                                                            <span className="type-badge">
                                                                {this.props.t(`app.notifications.${({ leave_request: 'leaveRequests', approval: 'approvals', reminder: 'reminders', update: 'updates', alert: 'alerts' })[notification.type] || notification.type}`)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="notification-meta">
                                                    <div className="notification-time">
                                                        {moment(notification.notificationDate).format("MMM DD, YYYY HH:mm")}
                                                    </div>
                                                </div>
                                            </div>
                                        </List.Content>
                                    </List.Item>
                                ))}
                            </List>

                            {filteredNotifications.length > 0 && (
                                <div className="pagination-container">
                                    {hasMoreNotifications ? (
                                        <div className="load-more-container">
                                            <Button
                                                fluid
                                                onClick={this.handleLoadMore}
                                                loading={loading || isLoadingMore}
                                                className="load-more-btn"
                                                icon="plus"
                                                content={this.props.t("app.notifications.loadMoreCount", { count: notifications.length })}
                                            />
                                        </div>
                                    ) : (
                                        <div className="pagination-info">
                                            {this.props.t("app.notifications.allLoaded", { count: notifications.length })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </Segment>
            </Container>
        );
    }

    getNotificationIcon = (type) => {
        const iconMap = {
            'leave_request': 'calendar',
            'approval': 'check circle',
            'system': 'cog',
            'reminder': 'clock',
            'update': 'info circle',
            'alert': 'exclamation triangle'
        };
        return iconMap[type] || 'bell';
    };
}

const mapStateToProps = (state) => ({
    notificationReducer: state.notificationReducer,
    currentUser: state.accountReducer?.userInfo,
});

const mapDispatchToProps = (dispatch) => ({
    notificationActions: bindActionCreators(notificationActions, dispatch),
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(NotificationsScreen));
